import { useCallback, useState } from 'react';
import type { FC } from 'react';

import classNames from 'classnames';

import type { ImageSources } from '../../helpers/images';

import * as styles from './Image.module.scss';

export interface ImageProps {
  imageSources?: ImageSources;
  onLoad?: () => void;
  className?: string;
  isRound?: boolean;
  isRounded?: boolean;
  lazy?: boolean;
  altText?: string;
}

const isCustomAspectRatio = (aspectRatio: ImageSources['aspectRatio']) => {
  return !['vertical', 'horizontal', 'square'].includes(aspectRatio);
};

const Image: FC<ImageProps> = ({
  imageSources,
  onLoad,
  className,
  isRound,
  isRounded = true,
  lazy = true,
  altText = '',
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const displayImage =
    imageSources && (imageSources.srcSet || imageSources.src);

  const onImageLoad = useCallback(() => {
    setIsLoaded(true);

    if (typeof onLoad === 'function') {
      onLoad();
    }
  }, [onLoad]);

  const image = displayImage ? (
    <img
      className={styles.image}
      alt={altText}
      sizes={imageSources.sizes}
      srcSet={imageSources.srcSet}
      src={imageSources.src}
      onLoad={onImageLoad}
      loading={lazy ? 'lazy' : undefined}
    />
  ) : null;

  return (
    <div
      className={classNames(
        styles.container,
        {
          [styles.isVertical]:
            imageSources && imageSources.aspectRatio === 'vertical',
          [styles.isSquare]:
            imageSources && imageSources.aspectRatio === 'square',
          [styles.isLoaded]: isLoaded,
          [styles.isRounded]: isRounded && !isRound,
          [styles.isRound]: isRound,
        },
        className
      )}
    >
      <div
        className={styles.aspectRatio}
        style={{
          aspectRatio:
            imageSources && isCustomAspectRatio(imageSources.aspectRatio)
              ? imageSources.aspectRatio
              : undefined,
        }}
      >
        {image}
      </div>
    </div>
  );
};

export { Image };
